import {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {useLocation} from 'react-router-dom'
import {WithChildren, isTokenExpired} from '../../../../_metronic/helpers'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import * as authHelper from './AuthHelpers'
import {AuthModel, UserModel} from './_models'
import {logoutUser} from './_requests'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const sessionReplacementHandled = useRef(false)
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    const {token} = authHelper.setupAxios()
    sessionStorage.removeItem('intervalId')

    if (token && !isTokenExpired(token)) {
      void logoutUser().catch(() => undefined)
    }

    saveAuth(undefined)
    setCurrentUser(undefined)
    sessionStorage.setItem('isLoggedIn', 'false')
  }

  useEffect(() => {
    sessionReplacementHandled.current = false
    return authHelper.subscribeToAuthSessionLogin((event) => {
      if (!auth?.id || event.userId !== auth.id || sessionReplacementHandled.current) return

      sessionReplacementHandled.current = true
      authHelper.removeAuth()
      setAuth(undefined)
      setCurrentUser(undefined)
      sessionStorage.removeItem('currentUserRole')
      sessionStorage.removeItem('isLoggedIn')
      sessionStorage.removeItem('isUserLoggedOut')

      void Swal.fire({
        icon: 'warning',
        title: 'You were signed out',
        text: 'Your account was signed in from another tab or device. Please sign in again.',
        confirmButtonText: 'Sign in again',
        allowOutsideClick: false,
        allowEscapeKey: false,
        heightAuto: false,
      }).then(() => {
        const frontendPath = (process.env.PUBLIC_URL || '').replace(/\/$/, '')
        window.location.replace(`${window.location.origin}${frontendPath}/login`)
      })
    })
  }, [auth?.id])

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const location = useLocation()
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          let data = JSON.parse(window.atob(apiToken.split('.')[1]))

          if (data) {
            data = auth
            setCurrentUser(data)
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.token) {
      requestUser(auth.token)
    } else {
      logout()
      setShowSplashScreen(false)
    }
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthInit, AuthProvider, useAuth}
